// Header search overlay. Alpine component delegating all search concerns to
// SearchCore; only adds the modal chrome (open/close/focus trap, top offset,
// infinite-scroll observer rooted at the dialog) and URL sync so search
// queries are shareable and bookmarkable.
//
// URL sync:
//   - On page load, ?search=<query> auto-opens the popup with that query.
//   - Typing in the input updates ?search=<query> on the browser URL.
//   - Closing the popup removes ?search from the URL.
//
// A11y responsibilities (the rest live in the Fusion template):
//   - openModal() remembers the triggering element; closeModal() returns focus to it.
//   - The visible status string is mirrored in a polite aria-live region bound
//     to the `announcement` getter - count only, never the list body.
//   - The focus trap itself is x-trap.noscroll.inert in the template
//     (from @alpinejs/focus).
import Alpine from '../../../../CodeQ.Site/Resources/Private/Fusion/node_modules/alpinejs';
import { createSearchCore, decorateHit, MIN_CHARS } from './SearchCore.js';

// Below this breakpoint cover the full viewport; above, anchor the overlay to
// the bottom of the real header element. Mirrors the `hb` token from
// tailwind.config.js.
const HB_BREAKPOINT = 1080;

const SEARCH_PARAM = 'search';

const updateUrl = (query) => {
    const url = new URL(window.location.href);
    if (query) {
        url.searchParams.set(SEARCH_PARAM, query);
    } else {
        url.searchParams.delete(SEARCH_PARAM);
    }
    window.history.replaceState({}, '', url.toString());
};

const readQueryFromUrl = () => {
    try {
        return new URL(window.location.href).searchParams.get(SEARCH_PARAM) || '';
    } catch (_) {
        return '';
    }
};

Alpine.data(
    'searchPopup',
    ({
        host = '',
        apiKey = '',
        indexName = '',
        siteNodePath = '',
        mockHits = null,
        mockQuery = '',
        labels = {},
    } = {}) => {
        const data = createSearchCore({
            host,
            apiKey,
            indexName,
            siteNodePath,
            labels,
            logPrefix: 'SearchPopup',
            // Reflect the current query into the URL so ?search=foo is shareable.
            // Only page-0 responses move the URL; paginated loads keep the same query.
            onSearchSuccess: (q, page) => {
                if (page === 0) updateUrl(q);
            },
            // Input cleared to below the min-char threshold? Drop the param so a
            // bare URL does not pin a stale query.
            onShortQuery: () => updateUrl(''),
        });

        // Monocle has no site node or tenant token. Its fixture stays entirely in
        // the browser and never sends a request to Meilisearch.
        if (mockHits) {
            const fixtureHits = Object.values(mockHits);
            data.ensureClient = () => {};
            data._runSearch = function (q, page = 0) {
                if (page > 0) return;
                const needle = q.toLocaleLowerCase('de');
                const matches = fixtureHits.filter((hit) =>
                    `${hit.title || ''} ${hit.__fulltext?.text || ''}`
                        .toLocaleLowerCase('de')
                        .includes(needle)
                );
                this.hits = matches.map(decorateHit);
                this.resultCount = matches.length;
                this.page = 0;
                this.error = null;
                this.loading = false;
                updateUrl(q);
            };
        }

        // Closure-scoped so Alpine does not reactively wrap them.
        let trigger = null;
        let intersectionObserver = null;
        // Track the RAF + setTimeout IDs scheduled by openModal so closeModal
        // can cancel them. Without this, a fast Open -> Escape leaves a pending
        // focus() call pointed at a hidden input and schedules observer setup
        // on a now-dismissed dialog.
        let openAnimationFrame = null;
        let openFocusTimeout = null;

        const computeTopOffset = () => {
            if (window.innerWidth < HB_BREAKPOINT) return 0;
            const header = document.querySelector('header');
            if (!header) return 0;
            return Math.round(header.getBoundingClientRect().bottom);
        };

        data.showSearch = false;
        data.topOffset = 0;

        data.init = function () {
            if (!host || !siteNodePath) {
                console.warn('[SearchPopup] host and siteNodePath are required');
            }
            // Keep topOffset in sync on resize - header height can change across breakpoints.
            window.addEventListener('resize', () => {
                if (this.showSearch) this.topOffset = computeTopOffset();
            });

            // Deep-link: if the page was opened with ?search=foo, open the popup
            // and seed the query. Legacy /suche?search=foo URLs redirect to
            // /?search=foo and land here.
            const initialQuery = readQueryFromUrl();
            if (initialQuery && initialQuery.trim().length >= MIN_CHARS) {
                this.openModal();
                this.query = initialQuery;
                this.runSearchNow();
            }
        };

        data.openModal = function () {
            // Guard against double-open (eg. the trigger button fires twice or
            // user hits Enter while already open). Without this, we'd re-schedule
            // focus and leak the previous RAF/setTimeout IDs.
            if (this.showSearch) return;
            // Remember whatever had focus (usually the header search icon
            // button) so we can return to it on close. Required for keyboard users.
            trigger = document.activeElement;
            // Move focus off the trigger BEFORE x-trap flips aria-hidden on the
            // rest of the page. Otherwise the still-focused trigger sits inside
            // an aria-hidden ancestor for the ~100 ms between showSearch=true and
            // the deferred input.focus() below, which the browser flags with
            // "Blocked aria-hidden on an element because its descendant retained
            // focus". Focus goes to <body>; closeModal() will restore it later.
            if (trigger && typeof trigger.blur === 'function') trigger.blur();
            this.ensureClient();
            this.topOffset = computeTopOffset();
            this.showSearch = true;
            document.body.classList.add('overflow-hidden');
            if (mockHits && !this.query && mockQuery) {
                this.query = mockQuery;
                this.runSearchNow();
            }

            // x-trap focuses the first focusable element (the close button).
            // We want the input. Defer to after x-show has rendered the dialog.
            openAnimationFrame = requestAnimationFrame(() => {
                openAnimationFrame = null;
                openFocusTimeout = setTimeout(() => {
                    openFocusTimeout = null;
                    this.$refs.input?.focus();
                    // Attach the intersection observer once the dialog is laid
                    // out so we can observe the sentinel against the dialog's
                    // own scroll container.
                    this._setupInfiniteScroll();
                }, 100);
            });
        };

        data._setupInfiniteScroll = function () {
            if (intersectionObserver) return;
            const sentinel = this.$refs.sentinel;
            const root = this.$refs.dialog;
            if (!sentinel || !root) return;
            intersectionObserver = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (
                            entry.isIntersecting &&
                            this.hasMore &&
                            !this.loading &&
                            this.hits.length > 0
                        ) {
                            this._runSearch(this.query.trim(), this.page + 1);
                        }
                    }
                },
                { root, rootMargin: '400px' }
            );
            intersectionObserver.observe(sentinel);
        };

        data.closeModal = function () {
            // Idempotent: closing an already-closed modal is a no-op.
            // Prevents re-focusing the stored trigger when Escape fires twice.
            if (!this.showSearch) return;

            // Cancel any pending openModal schedulers so they do not fire on a
            // now-hidden dialog.
            if (openAnimationFrame !== null) {
                cancelAnimationFrame(openAnimationFrame);
                openAnimationFrame = null;
            }
            if (openFocusTimeout !== null) {
                clearTimeout(openFocusTimeout);
                openFocusTimeout = null;
            }

            this.showSearch = false;
            this.query = '';
            this.hits = [];
            this.resultCount = null;
            this.error = null;
            this.loading = false;
            this.page = 0;
            this.clearDebounce();
            document.body.classList.remove('overflow-hidden');
            // Drop ?search from the URL so reopening / sharing the current
            // page is clean again.
            updateUrl('');

            if (intersectionObserver) {
                intersectionObserver.disconnect();
                intersectionObserver = null;
            }

            // Return focus to the trigger. Alpine's x-trap does not do this
            // for us here because we hide via x-show, not by removing the trap.
            if (trigger && typeof trigger.focus === 'function') {
                trigger.focus();
            }
        };

        return data;
    }
);
